import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, Download, Lock, FileText, AlertTriangle } from "lucide-react";

export default function SharePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(undefined);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/share/${token}`).then((r) => setMeta(r.data))
      .catch((e) => { setMeta(null); setError(formatApiErrorDetail(e?.response?.data?.detail) || "This link is unavailable."); });
  }, [token]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.post(`/share/${token}/download`, { password }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${meta?.type_label || "document"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Download failed.";
      setError(typeof detail === "string" ? detail : "Download failed.");
    } finally { setDownloading(false); }
  };

  if (meta === undefined) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;

  const primary = meta?.color_primary || "#0A192F";
  const accent = meta?.color_accent || "#0052FF";

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-2xl shadow-sm max-w-md w-full p-8 text-center" data-testid="share-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-bold text-lg text-slate-800">Link unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white rounded-2xl shadow-sm max-w-md w-full overflow-hidden" data-testid="share-page">
        <div style={{ background: primary }} className="p-6 text-white">
          <div className="text-lg font-extrabold">{meta.firm_name}</div>
          <div className="text-xs opacity-80">Secure document delivery</div>
        </div>
        <div style={{ height: 4, background: accent }} />
        <div className="p-6">
          <div className="flex items-center gap-2 text-slate-800 font-semibold"><FileText className="w-5 h-5" style={{ color: accent }} /> {meta.type_label}</div>
          <p className="text-sm text-slate-500 mt-1">{meta.subject}</p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-500">
            <div><span className="block text-slate-400">Matter</span>{meta.case_number || "—"}</div>
            <div><span className="block text-slate-400">Prepared by</span>{meta.attorney_name || "—"}</div>
            <div><span className="block text-slate-400">Package ID</span>{meta.package_id}</div>
            <div><span className="block text-slate-400">Report ID</span>{meta.report_id}</div>
          </div>
          <div className="mt-3 text-[10px] font-mono text-slate-400 break-all">SHA-256: {meta.content_hash}</div>
          {meta.expires_at && <div className="mt-1 text-[11px] text-slate-400">Link expires {new Date(meta.expires_at).toLocaleDateString()}</div>}

          {meta.requires_password && (
            <div className="mt-4">
              <label className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Password required</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="share-password" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-slate-400" />
            </div>
          )}
          {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

          <button onClick={download} disabled={downloading} data-testid="share-download"
            style={{ background: accent }} className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download document
          </button>
          <div className="mt-4 flex items-start gap-2 text-[10px] text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Sent securely on behalf of {meta.firm_name}. This document is confidential and intended only for the authorized recipient.
          </div>
        </div>
      </div>
    </div>
  );
}
